import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom X (Twitter) icon component
const XIcon = ({ size = 18, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const socialIconMap = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  twitter: XIcon
};

export default function Footer() {
  const [footerSettings, setFooterSettings] = useState({
    logo_url: 'https://customer-assets.emergentagent.com/job_e8fbeb9b-6487-4b78-a6f7-665b46ef2f3a/artifacts/bk3s60pn_Circuit%207%20buloke%20logo%20%282%29.png',
    tagline: 'Where ambition meets momentum. Premium executive coworking & private offices in Bangalore.',
    quick_links: [],
    contact_email: 'info@thecircuit.work',
    contact_email_link: 'mailto:info@thecircuit.work',
    contact_phone: '+91 98765 43210',
    contact_phone_link: 'tel:+919876543210',
    contact_address: 'Sarjapur Road & Hebbal, Bangalore',
    contact_address_link: 'https://maps.google.com/?q=Bangalore,India',
    social_links: [],
    copyright_text: '© 2024 The Circuit. All rights reserved.',
    padding_top: '90px',
    padding_bottom: '64px',
    padding_left: '16px',
    padding_right: '16px'
  });

  useEffect(() => {
    const fetchFooterSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/footer-settings`);
        if (response.data) {
          setFooterSettings(prevSettings => ({
            ...prevSettings,
            ...response.data
          }));
        }
      } catch (error) {
        console.error('Error fetching footer settings:', error);
      }
    };

    fetchFooterSettings();
  }, []);

  return (
    <footer className="bg-[#1a1a1a] text-white">
      <div 
        className="container mx-auto" 
        style={{
          paddingTop: footerSettings.padding_top,
          paddingBottom: footerSettings.padding_bottom,
          paddingLeft: footerSettings.padding_left,
          paddingRight: footerSettings.padding_right
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <div className="mb-6">
              <img 
                src={footerSettings.logo_url} 
                alt="The Circuit Logo" 
                className="h-20 mb-4"
              />
            </div>
            <p className="text-gray-400 text-base mb-6 leading-relaxed">
              {footerSettings.tagline}
            </p>
            {footerSettings.social_links && footerSettings.social_links.length > 0 && (
              <div className="flex space-x-4">
                {footerSettings.social_links.map((social, index) => {
                  const Icon = socialIconMap[social.platform.toLowerCase()] || Facebook;
                  return (
                    <a 
                      key={index}
                      href={social.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#E12429] transition-all"
                    >
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="mt-6 md:mt-0">
            <h3 className="text-xl font-semibold mb-6 text-white">Quick Links</h3>
            <ul className="space-y-3">
              {footerSettings.quick_links && footerSettings.quick_links.length > 0 ? (
                footerSettings.quick_links.map((link, index) => (
                  <li key={index}>
                    {link.url.startsWith('http') ? (
                      <a 
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-[#E12429] transition-colors text-base"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link 
                        to={link.url} 
                        className="text-gray-400 hover:text-[#E12429] transition-colors text-base"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))
              ) : (
                <>
                  <li><Link to="/" className="text-gray-400 hover:text-[#E12429] transition-colors text-base">Home</Link></li>
                  <li><Link to="/about" className="text-gray-400 hover:text-[#E12429] transition-colors text-base">About Us</Link></li>
                  <li><Link to="/find-space" className="text-gray-400 hover:text-[#E12429] transition-colors text-base">Find a Space</Link></li>
                  <li><Link to="/blog" className="text-gray-400 hover:text-[#E12429] transition-colors text-base">Blog</Link></li>
                  <li><Link to="/gallery" className="text-gray-400 hover:text-[#E12429] transition-colors text-base">Gallery</Link></li>
                </>
              )}
            </ul>
          </div>

          {/* Contact */}
          <div className="mt-6 md:mt-0">
            <h3 className="text-xl font-semibold mb-6 text-white">Contact Us</h3>
            <ul className="space-y-4 text-base">
              {footerSettings.contact_phone && (
                <li className="flex items-start space-x-3">
                  <Phone size={18} className="mt-1 flex-shrink-0 text-[#E12429]" />
                  {footerSettings.contact_phone_link ? (
                    <a 
                      href={footerSettings.contact_phone_link}
                      className="text-gray-400 hover:text-[#E12429] transition-colors"
                    >
                      {footerSettings.contact_phone}
                    </a>
                  ) : (
                    <span className="text-gray-400">{footerSettings.contact_phone}</span>
                  )}
                </li>
              )}
              {footerSettings.contact_email && (
                <li className="flex items-start space-x-3">
                  <Mail size={18} className="mt-1 flex-shrink-0 text-[#E12429]" />
                  {footerSettings.contact_email_link ? (
                    <a 
                      href={footerSettings.contact_email_link}
                      className="text-gray-400 hover:text-[#E12429] transition-colors"
                    >
                      {footerSettings.contact_email}
                    </a>
                  ) : (
                    <span className="text-gray-400">{footerSettings.contact_email}</span>
                  )}
                </li>
              )}
              {footerSettings.contact_address && (
                <li className="flex items-start space-x-3">
                  <MapPin size={18} className="mt-1 flex-shrink-0 text-[#E12429]" />
                  {footerSettings.contact_address_link ? (
                    <a 
                      href={footerSettings.contact_address_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-[#E12429] transition-colors"
                      style={{ whiteSpace: 'pre-line' }}
                    >
                      {footerSettings.contact_address}
                    </a>
                  ) : (
                    <span className="text-gray-400" style={{ whiteSpace: 'pre-line' }}>
                      {footerSettings.contact_address}
                    </span>
                  )}
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 text-center">
          <p className="text-gray-400 text-sm">
            {footerSettings.copyright_text || `© ${new Date().getFullYear()} The Circuit. All rights reserved.`}
          </p>
        </div>
      </div>
    </footer>
  );
}